import { useState, useEffect } from 'react';
import { allTags, SingleTag } from './tagTypes';

interface UseTagsReturn {
  tags: SingleTag[];
  isLoading: boolean;
  error: string | null;
  refreshTags: () => Promise<void>;
}

/**
 * Custom hook to manage tags initialization and provide tags data
 * This ensures the allTags singleton is initialized only once across the app
 */
export function useTags(): UseTagsReturn {
  const [tags, setTags] = useState<SingleTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize tags on mount
  useEffect(() => {
    const initializeTags = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Check if already initialized
        if (!allTags.isTagsInitialized()) {
          await allTags.initialize();
        }
        
        const allTagsData = allTags.getAllTags();
        setTags(allTagsData);
      } catch (err) {
        console.error('Error initializing tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      } finally {
        setIsLoading(false);
      }
    };

    initializeTags();
  }, []);

  // Function to refresh tags
  const refreshTags = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await allTags.refreshTags();
      const allTagsData = allTags.getAllTags();
      setTags(allTagsData);
    } catch (err) {
      console.error('Error refreshing tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh tags');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    tags,
    isLoading,
    error,
    refreshTags
  };
}

/**
 * Hook to get a specific tag name by ID
 * This is useful for components that only need to resolve individual tag names
 */
export function useTagName(tagId: number): string | undefined {
  const [tagName, setTagName] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getTagName = async () => {
      try {
        setIsLoading(true);
        
        // Check if already initialized
        if (!allTags.isTagsInitialized()) {
          await allTags.initialize();
        }
        
        const name = allTags.getTagName(tagId);
        setTagName(name);
      } catch (err) {
        console.error('Error getting tag name:', err);
        setTagName(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    getTagName();
  }, [tagId]);

  return tagName;
}

/**
 * Hook to get multiple tag names by IDs
 * This is useful for components that need to resolve multiple tag names
 */
export function useTagNames(tagIds: number[]): (string | undefined)[] {
  const [tagNames, setTagNames] = useState<(string | undefined)[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getTagNames = async () => {
      try {
        setIsLoading(true);
        
        // Check if already initialized
        if (!allTags.isTagsInitialized()) {
          await allTags.initialize();
        }
        
        const names = tagIds.map(tagId => allTags.getTagName(tagId));
        setTagNames(names);
      } catch (err) {
        console.error('Error getting tag names:', err);
        setTagNames(tagIds.map(() => undefined));
      } finally {
        setIsLoading(false);
      }
    };

    if (tagIds.length > 0) {
      getTagNames();
    } else {
      setTagNames([]);
      setIsLoading(false);
    }
  }, [tagIds]);

  return tagNames;
}
